import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Container } from 'react-bootstrap';
import {
  clearErrorMessage, renewalOrder
} from "../../redux/actions/productActions";
import {useDispatch} from "react-redux";
import ProductHeader from "../product/ProductHeader";
import ProductStep from "../product/ProductStep";
import RenewalStep1 from "./RenewalStep1";
import RenewalStep2 from "./RenewalStep2";
import RenewalStep3 from "./RenewalStep3";
import RenewalStep4 from "./RenewalStep4";
import { getUA, isMobile } from "react-device-detect";
import { useRef, useCallback, useEffect } from "react";
import { usePrevious } from "../../hooks";
import { config } from '_config';

const steps = ["Step 1", "Step 2", "Step 3", "Step 4"];

export default function Renewal({
  state,
  onChange,
  onBlur,
  onStep,
  trademarkRushProcessing,
  onHandleCategoriesStillInUse,
  onHandleCategoriesNoLongerInUse,
  imageSpecimen,
  imageSpecimenSrc,
  allowedExtensions,
}) {
  const handleChange = useCallback(
    (e) => {
      onChange && onChange(e);
    },
    [onChange]
  );

  const [validated, setValidated] = React.useState(false);
  const [suspendedAction, setSuspendedAction] = React.useState(false);

  const prevState = usePrevious(state);

  const handleNext = useCallback(() => {
    if (state.verifying) {
      return;
    }

    setSuspendedAction(false);

    localStorage.setItem('product', JSON.stringify(state));
    localStorage.setItem('path', 'trademark-renewal');

    setValidated(true);

    const section1 = document.querySelector('.is-invalid');
    if (section1 != null) {
      section1.scrollIntoView();
    } else {
      let form = document.querySelector('#TrademarkForm');
      if (form.checkValidity() === false) {
        document.querySelector('#TrademarkForm').classList.add('was-validated');
        let section = document.querySelector('.form-control:invalid');
        if (section) {
          section.scrollIntoView();
        } else {
          section = document.querySelector('.form-check-input:invalid');
          section.scrollIntoView();
        }
      } else {
        setValidated(false);
        if (state.step === steps.length - 1) {
          handleSubmit();
          return;
        }
        onStep && onStep(state.step + 1);
        titleRef.current.scrollIntoView();
      }
    }
  }, [state, onStep, handleSubmit]);

  const handleBack = useCallback(() => {
    onStep && onStep(state.step - 1);
    titleRef.current.scrollIntoView();
  }, [state, onStep]);

  const handleReset = useCallback(() => {
    onStep && onStep(0);
  }, [onStep]);

  const dispatch = useDispatch();
  const titleRef = useRef();

  function handleSubmit() {
    dispatch(clearErrorMessage());
    const productInfo = {
      ...state,
      device: isMobile && !getUA.includes('Intel') ? 'MOBILE' : 'DESKTOP',
    };
    dispatch(renewalOrder(productInfo, imageSpecimen));
  }

  useEffect(() => {
    if (prevState?.verifying && !state.verifying && suspendedAction) {
      handleNext();
    }
  }, [prevState, state, suspendedAction, handleNext]);

  return (
    <Box sx={{ width: '100%' }}>
      <Container className="md-container" ref={titleRef}>
        <Container style={{
          paddingLeft: config.skin === 'v2-basic' ? 0 : '12px',
          paddingRight: config.skin === 'v2-basic' ? 0 : '12px',
        }}
        >
          {config.skin !== 'v2-basic' && (
            <>
            <ProductHeader header="Renewal Filing" />
            <ProductStep activeStep={state.step} steps={steps}/>
            </>
          )}

          {state.step === steps.length ? (
            <React.Fragment>
              <Typography sx={{ mt: 2, mb: 1 }}>
                All steps completed - you&apos;re finished
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button onClick={handleReset}>Reset</Button>
              </Box>
            </React.Fragment>
          ) : null}

          {state.step === 0 ? (
            <RenewalStep1
              state={state}
              onChange={handleChange}
              onBlur={onBlur}
              validated={validated}
            />
          ) : null}
          {state.step === 1 ? (
            <RenewalStep2
              state={state}
              onChange={handleChange}
              validated={validated}
              onHandleCategoriesStillInUse={onHandleCategoriesStillInUse}
              onHandleCategoriesNoLongerInUse={onHandleCategoriesNoLongerInUse}
              imageSpecimenSrc={imageSpecimenSrc}
              allowedExtensions={allowedExtensions}
            />
          ) : null}
          {state.step === 2 ? (
            <RenewalStep3
              state={state}
              onChange={handleChange}
              onBlur={onBlur}
              validated={validated}
            />
          ) : null}
          {state.step === 3 ? (
            <RenewalStep4
              state={state}
              onChange={handleChange}
              validated={validated}
              trademarkRushProcessing={trademarkRushProcessing}
            />
          ) : null}

          <Box sx={{ 
            display: 'flex',
            flexDirection: 'row',
            justifyContent: config.skin === 'v2-basic' ? 'flex-end' : 'flex-start',
            flexWrap: config.skin === 'v2-basic' ? 'wrap' : 'unset',
            pt: 1,
            pb: 1,
          }}
          >
            {config.skin === 'v2-basic' && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', pt: 1, pb: 1, opacity: 0.9 }}>
                  <Box sx={{ display: 'flex', mr: 1 }}>
                    <img
                      src={`${config.basePath}/images/lock-dark.png`}
                      alt=""
                      layout="fill"
                    />
                  </Box>
                  <span className='lock-text'>Click on "Continue" to save your application</span>
                </Box>
              </Box>
            )}

            {state.step === 0 ? null : (
              <Button
                className="prev"
                disabled={state.step === 0}
                onClick={handleBack}
                onMouseDown={() => setSuspendedAction(true)}
                onTouchStart={() => setSuspendedAction(true)}
                variant="outlined"
                sx={{ mr: 1, mt: 1, mb: 1 }}
              >
                Previous
              </Button>
            )}

            <Button
              onClick={handleNext}
              onMouseDown={() => setSuspendedAction(true)}
              onTouchStart={() => setSuspendedAction(true)}
              variant="outlined"
              sx={{ mt: 1, mb: 1 }}
            >
              {state.step === steps.length - 1 ? 'Continue' : 'Continue'}
            </Button>
          </Box>
        </Container>
      </Container>
    </Box>
  );
}
